import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Dice5, Mail, Lock, User, Loader2, Gift } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";

export default function Login() {
  const { setUser } = useAuth();
  const { t, lang, toggle } = useLang();
  const nav = useNavigate();
  const refCode = new URLSearchParams(window.location.search).get("ref");
  const [mode, setMode] = useState(refCode ? "signup" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const ref = new URLSearchParams(window.location.search).get("ref") || undefined;
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { ...form, ref };
      const { data } = await api.post(path, payload);
      setUser(data);
      nav("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid-noise flex flex-col items-center justify-center px-4 py-10">
      <button onClick={toggle} className="absolute top-5 right-5 rounded-full border border-cyan-400/30 bg-black/30 px-3 py-1.5 text-xs font-bold text-cyan-300" data-testid="lang-toggle-btn">
        {lang === "hi" ? "EN" : "हिंदी"}
      </button>

      <Link to="/" className="mb-6 flex items-center gap-3">
        <img src="/brand_logo.jpg" alt="Bam Bam Bhole" className="h-16 w-16 rounded-2xl object-cover ring-1 ring-amber-500/40 animate-trishul" />
        <span className="font-display text-3xl font-extrabold gold-text">{t("brand")}</span>
      </Link>

      <div className="mw-card w-full max-w-md p-6 sm:p-8 relative z-10">
        <h1 className="font-display text-2xl font-bold mb-1">
          {mode === "login" ? t("welcome") : t("createAccount")}
        </h1>
        <p className="text-sm text-slate-400 mb-6">{t("loginToPlay")}</p>

        {refCode && (
          <div className="mb-5 flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300" data-testid="referral-banner">
            <Gift size={18} /> {t("inviteBonus")}
          </div>
        )}

        <button
          onClick={googleLogin}
          data-testid="google-login-btn"
          className="w-full flex items-center justify-center gap-3 rounded-xl border border-slate-600 bg-white text-slate-800 font-semibold py-2.5 hover:bg-slate-100 transition-colors"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
          {t("continueGoogle")}
        </button>

        <div className="my-5 flex items-center gap-3 text-xs text-slate-500">
          <div className="h-px flex-1 bg-slate-700" /> {t("or")} <div className="h-px flex-1 bg-slate-700" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <div className="flex items-center gap-2 rounded-xl border border-amber-500/25 bg-black/40 px-3">
              <User size={17} className="text-amber-400/70" />
              <input
                required
                placeholder={t("name")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="signup-name-input"
                className="w-full bg-transparent py-2.5 text-sm focus:outline-none"
              />
            </div>
          )}
          <div className="flex items-center gap-2 rounded-xl border border-amber-500/25 bg-black/40 px-3">
            <Mail size={17} className="text-amber-400/70" />
            <input
              required
              type="email"
              placeholder={t("email")}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="email-input"
              className="w-full bg-transparent py-2.5 text-sm focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-amber-500/25 bg-black/40 px-3">
            <Lock size={17} className="text-amber-400/70" />
            <input
              required
              type="password"
              placeholder={t("password")}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="password-input"
              className="w-full bg-transparent py-2.5 text-sm focus:outline-none"
            />
          </div>

          {error && <p className="text-sm text-rose-400" data-testid="auth-error">{error}</p>}

          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-btn"
            className="w-full btn-gold rounded-xl py-2.5 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 size={17} className="animate-spin" />}
            {mode === "login" ? t("login") : t("signup")}
          </button>
        </form>

        <p className="mt-5 text-center text-sm text-slate-400">
          {mode === "login" ? t("noAccount") : t("haveAccount")}{" "}
          <button
            onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); }}
            data-testid="auth-switch-btn"
            className="font-semibold text-amber-400 hover:underline"
          >
            {mode === "login" ? t("signup") : t("login")}
          </button>
        </p>
      </div>
      <p className="mt-6 text-xs text-slate-600 text-center max-w-md">{t("disclaimer")}</p>
      <Link to="/admin" data-testid="admin-login-link" className="mt-3 text-xs text-amber-400/70 hover:text-amber-300 underline">
        {t("adminLoginLink")}
      </Link>
    </div>
  );
}
